/**
 * /hk/training · Training Mode (attendant view)
 * ────────────────────────────────────────────────────────────────────
 * Voice-First digital trainer. Trainees learn by hearing, seeing,
 * tapping. Zero reading required. Every screen reuses the HK-OS
 * primitives already used by the live app — the ONLY thing that
 * changes between training and production is the data source.
 *
 * Backend contract:  /api/oa/v1/training/*
 *
 * Constitutional: no Bengali strings live in this file. Text arrives
 * from the OA Voice Registry at runtime.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  ArrowRight, Award, CheckCircle2, Loader2, Play, RefreshCw, Sparkles,
  Trophy, Clock, ShieldCheck, Lock, RotateCcw, PartyPopper, ClipboardList,
  User as UserIcon, PlayCircle, LayoutDashboard,
} from "lucide-react";
import { toast } from "sonner";
import {
  VoiceRegistryProvider,
  VoicePlayer,
  VoiceInstructionBar,
  NextTaskCard,
  ScanRoomButton,
  VoiceRecorderButton,
  PhotoEvidenceUploader,
  TaskCompletionButton,
  AttentionOverlay,
  OfflineSyncIndicator,
  VoiceWarmupManager,
  useVoiceRegistry,
} from "@/components/operational/hk_os";

const API = process.env.REACT_APP_BACKEND_URL;

// ─── API helper (cookie auth) ────────────────────────────────────────
const client = axios.create({ baseURL: `${API}/api/oa/v1`, withCredentials: true });

async function apiGet(path, params) {
  const { data } = await client.get(path, { params });
  return data;
}
async function apiPost(path, body) {
  const { data } = await client.post(path, body || {});
  return data;
}

// ─── Practice content: mimic live rooms ───────────────────────────────
const PRACTICE_ROOMS = ["305", "204", "812", "410"];

const ENCOURAGEMENT_KEYS = [
  "VOICE_EXCELLENT", "VOICE_WELL_DONE", "VOICE_GOOD_JOB",
  "VOICE_YOU_ARE_CORRECT",
];

function pickEncouragement() {
  return ENCOURAGEMENT_KEYS[Math.floor(Math.random() * ENCOURAGEMENT_KEYS.length)];
}

// ═══════════════════════════════════════════════════════════════════
// Home screen — module list + resume/reset + supervisor pill
// ═══════════════════════════════════════════════════════════════════
function ModuleTile({ mod, progress, locked, onOpen, testid }) {
  const status = progress?.status || "not_started";
  const badgeEarned = status === "completed";
  const Icon = badgeEarned ? Trophy : locked ? Lock : PlayCircle;
  const tone = locked
    ? "border-slate-200 bg-slate-50 text-slate-400"
    : badgeEarned
      ? "border-emerald-300 bg-emerald-50 text-emerald-900"
      : "border-sky-300 bg-white text-slate-900 hover:bg-sky-50";
  return (
    <div
      role="button"
      tabIndex={locked ? -1 : 0}
      aria-disabled={locked}
      onClick={() => !locked && onOpen(mod.id)}
      onKeyDown={(e) => {
        if (locked) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(mod.id);
        }
      }}
      data-testid={testid || `train-module-${mod.id}`}
      className={`text-left rounded-2xl border-2 p-5 flex items-center gap-4 min-h-[96px] transition-all active:scale-[0.98] ${locked ? "cursor-not-allowed" : "cursor-pointer"} focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-300 ${tone}`}
    >
      <span className={`shrink-0 rounded-2xl p-3 ${badgeEarned ? "bg-emerald-600 text-white" : locked ? "bg-slate-200 text-slate-500" : "bg-sky-600 text-white"}`}>
        <Icon className="w-7 h-7" strokeWidth={2.2} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-widest opacity-70">
          Module {mod.order}
        </div>
        <div className="text-[18px] font-bold leading-tight truncate">
          {mod.title}
        </div>
        <div className="mt-1 flex items-center gap-2 text-[12px] opacity-80"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}>
          <VoicePlayer voiceKey={mod.voice_intro_key} size="sm" showText={false}
                         testid={`${testid || `train-module-${mod.id}`}-voice`} />
          <span>{mod.lesson_count} {mod.lesson_count === 1 ? "step" : "steps"}</span>
          {badgeEarned && (
            <span className="ml-auto rounded-full bg-emerald-600 text-white px-2 py-0.5 text-[11px] font-bold">
              Done · {progress?.score}%
            </span>
          )}
          {locked && (
            <span className="ml-auto text-[11px] font-semibold">Locked</span>
          )}
        </div>
      </div>
      {!locked && !badgeEarned && (
        <ArrowRight className="w-6 h-6 opacity-60" strokeWidth={2.4} />
      )}
    </div>
  );
}

function HomeScreen({ session, modules, onStart, onModuleOpen, onAssessment, onReset, onCertify, isSupervisor, onOpenSupervisor }) {
  const { play } = useVoiceRegistry();
  const welcomeFired = useRef(false);

  useEffect(() => {
    if (!welcomeFired.current) {
      welcomeFired.current = true;
      play("VOICE_TRAINING_WELCOME").catch(() => {});
    }
  }, [play]);

  const hotelReady = session?.hotel_id;
  const progress = session?.progress_modules || {};
  const completedCount = modules.filter(
    (m) => progress[m.id]?.status === "completed"
  ).length;
  const allSevenDone = modules.slice(0, 7).every(
    (m) => progress[m.id]?.status === "completed"
  );
  const canCertify = session?.assessment?.passed && !session?.certification;

  const badgeCount = (session?.badges || []).length;

  return (
    <div className="space-y-6">
      <VoiceInstructionBar
        voiceKey="VOICE_TRAINING_WELCOME"
        taskIcon={Sparkles} variant="hero" autoPlay={false}
        progress={completedCount / modules.length}
        testid="training-welcome-bar"
      />

      {/* Trainee summary card */}
      <section className="rounded-3xl border-2 border-slate-200 bg-white p-6 flex flex-wrap items-center gap-4"
                data-testid="training-summary">
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-sky-100 text-sky-700 p-3">
            <UserIcon className="w-6 h-6" />
          </span>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
              Trainee
            </div>
            <div className="text-[18px] font-bold text-slate-900">
              {session?.attendant_email || "—"}
            </div>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <SummaryPill icon={ClipboardList} label="Modules"
                         value={`${completedCount}/${modules.length}`} testid="pill-modules" />
          <SummaryPill icon={Award} label="Badges"
                         value={badgeCount} testid="pill-badges" />
          <SummaryPill icon={Trophy} label="Score"
                         value={`${(session?.score || 0).toFixed(0)}%`} testid="pill-score" />
          <SummaryPill icon={Clock} label="Attempts"
                         value={session?.attempts_total || 0} testid="pill-attempts" />
        </div>
      </section>

      {/* Primary CTA */}
      {!session && (
        <TaskCompletionButton
          voiceKey="VOICE_TRAINING_STARTED"
          icon={Play} tone="primary" onClick={onStart}
          testid="training-start-btn"
        />
      )}

      {session && !canCertify && (
        <div className="grid sm:grid-cols-2 gap-3">
          <TaskCompletionButton
            voiceKey="VOICE_NEXT_TASK"
            icon={PlayCircle} tone="primary"
            onClick={() => {
              // Jump to the next incomplete module
              const nxt = modules.find(
                (m) => (progress[m.id]?.status || "not_started") !== "completed"
              );
              if (nxt) onModuleOpen(nxt.id);
            }}
            testid="training-resume-btn"
          />
          {allSevenDone && !session?.assessment?.passed && (
            <TaskCompletionButton
              voiceKey="VOICE_ASSESSMENT_START"
              icon={ShieldCheck} tone="success" onClick={onAssessment}
              testid="training-assessment-btn"
            />
          )}
        </div>
      )}

      {canCertify && (
        <TaskCompletionButton
          voiceKey="VOICE_CERTIFIED"
          icon={Award} tone="success" onClick={onCertify}
          testid="training-certify-btn"
        />
      )}

      {/* Module grid */}
      <section>
        <h2 className="text-[16px] font-bold text-slate-900 mb-3">
          Modules
        </h2>
        <div className="grid gap-3">
          {modules.map((m, i) => {
            const isFirst = i === 0;
            const prevDone = isFirst || progress[modules[i - 1]?.id]?.status === "completed";
            const locked = !prevDone;
            return (
              <ModuleTile
                key={m.id} mod={m} locked={locked && hotelReady !== undefined}
                progress={progress[m.id]}
                onOpen={onModuleOpen}
                testid={`train-module-${m.id}`}
              />
            );
          })}
        </div>
      </section>

      {/* Advanced actions */}
      <div className="flex flex-wrap items-center gap-2 pt-2">
        {session && (
          <button
            type="button"
            onClick={onReset}
            className="rounded-full border border-slate-300 bg-white text-slate-700 px-3 py-1.5 text-[12px] font-semibold hover:bg-slate-100"
            data-testid="training-reset-btn"
          >
            <RotateCcw className="inline w-3.5 h-3.5 mr-1" /> Reset training
          </button>
        )}
        {isSupervisor && (
          <button
            type="button"
            onClick={onOpenSupervisor}
            className="rounded-full border border-slate-300 bg-white text-slate-700 px-3 py-1.5 text-[12px] font-semibold hover:bg-slate-100"
            data-testid="training-open-supervisor-btn"
          >
            <LayoutDashboard className="inline w-3.5 h-3.5 mr-1" /> Supervisor board
          </button>
        )}
      </div>
    </div>
  );
}

function SummaryPill({ icon: Icon, label, value, testid }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5"
          data-testid={testid}>
      <Icon className="w-4 h-4 text-slate-500" strokeWidth={2.4} />
      <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
      <span className="text-[14px] font-bold text-slate-900 tabular-nums">{value}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Module Runner — drives one module's lessons via HK-OS primitives
// ═══════════════════════════════════════════════════════════════════
function ModuleRunner({ session, moduleId, modules, onExit, onFinishedModule }) {
  const { play } = useVoiceRegistry();
  const [lessons, setLessons] = useState([]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [encouragement, setEncouragement] = useState(null);
  const currentModule = modules.find((m) => m.id === moduleId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Fetch full module (with lessons) via a lightweight probe: the
      // /training/modules endpoint returned lesson_count only, so hit
      // the assessment endpoint pattern or reuse the module data by id.
      // For P5 we hardcode step-count from summary and use action per step.
      const summary = modules.find((m) => m.id === moduleId);
      if (!summary) return;
      // Reconstruct lessons client-side from module metadata: server
      // sends `lesson_count`. We reuse the summary's voice_intro_key for
      // every lesson which is safe — the server records real
      // module_id + lesson_id.
      const built = Array.from({ length: summary.lesson_count }, (_, i) => ({
        id: `l${i + 1}`,
        action: MODULE_ACTIONS[moduleId]?.[i] || "start_task",
        voice_key: MODULE_VOICE_KEYS[moduleId]?.[i] || summary.voice_intro_key,
      }));
      if (!cancelled) {
        setLessons(built);
        setStep(0);
        try {
          await apiPost(`/training/session/${session.id}/module/${moduleId}/start`);
        } catch {
          /* ignore */
        }
      }
    })();
    return () => { cancelled = true; };
  }, [moduleId, modules, session.id]);

  const currentLesson = lessons[step];
  const room = PRACTICE_ROOMS[step % PRACTICE_ROOMS.length];

  const emitAttempt = useCallback(async (success) => {
    if (busy || !currentLesson) return;
    setBusy(true);
    try {
      await apiPost(`/training/session/${session.id}/lesson/attempt`, {
        module_id: moduleId,
        lesson_id: currentLesson.id,
        action: currentLesson.action,
        success,
      });
      if (success) {
        const key = pickEncouragement();
        setEncouragement(key);
        play(key).catch(() => {});
        setTimeout(() => setEncouragement(null), 1400);
        if (step + 1 >= lessons.length) {
          // Complete module
          const r = await apiPost(`/training/session/${session.id}/module/${moduleId}/complete`);
          play("VOICE_BADGE_EARNED").catch(() => {});
          onFinishedModule(r.session, r.score);
        } else {
          setStep(step + 1);
        }
      } else {
        play("VOICE_RETRY").catch(() => {});
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Attempt failed");
    } finally {
      setBusy(false);
    }
  }, [busy, currentLesson, session.id, moduleId, step, lessons.length, play, onFinishedModule]);

  if (!lessons.length) {
    return (
      <div className="min-h-[300px] flex items-center justify-center text-slate-500 gap-3"
             data-testid="module-loading">
        <Loader2 className="w-6 h-6 animate-spin" /> Loading module…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={`module-runner-${moduleId}`}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onExit}
          data-testid="module-exit-btn"
          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-100"
        >
          ← Back
        </button>
        <div className="text-[12px] font-bold uppercase tracking-widest text-slate-500">
          Module {currentModule?.order} · {currentModule?.title}
        </div>
        <div className="ml-auto text-[13px] font-bold text-slate-700 tabular-nums"
              data-testid="module-step-counter">
          {step + 1} / {lessons.length}
        </div>
      </div>

      <VoiceInstructionBar
        voiceKey={currentLesson.voice_key}
        variant="hero" autoPlay
        progress={step / lessons.length}
        taskIcon={Sparkles}
        testid="module-instruction"
      />

      <LessonSurface
        action={currentLesson.action}
        room={room}
        onSuccess={() => emitAttempt(true)}
        onFailure={() => emitAttempt(false)}
      />

      {encouragement && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-emerald-600/95 text-white pointer-events-none"
              data-testid="encouragement-overlay">
          <div className="text-center">
            <PartyPopper className="w-20 h-20 mx-auto mb-3" />
            <VoicePlayer voiceKey={encouragement} size="lg" showText
                           testid="encouragement-voice" />
          </div>
        </div>
      )}
    </div>
  );
}

const MODULE_ACTIONS = {
  m1_scan_room:   ["scan_room", "scan_room"],
  m2_cleaning:    ["start_task", "take_photo", "finish_task"],
  m3_photos:      ["take_photo", "take_photo"],
  m4_voice_notes: ["record_note", "record_note"],
  m5_lost_found:  ["log_lost_found", "take_photo"],
  m6_inspection:  ["inspect_pass", "inspect_fail"],
  m7_emergency:   ["acknowledge_emergency"],
};
const MODULE_VOICE_KEYS = {
  m1_scan_room:   ["VOICE_SCAN_ROOM", "VOICE_SCAN_ROOM"],
  m2_cleaning:    ["VOICE_START_TASK", "VOICE_PHOTO_REQUIRED", "VOICE_FINISH_TASK"],
  m3_photos:      ["VOICE_PHOTO_REQUIRED", "VOICE_PHOTO_REQUIRED"],
  m4_voice_notes: ["VOICE_RECORD_NOTE", "VOICE_DEFECT_FOUND"],
  m5_lost_found:  ["VOICE_LOST_FOUND", "VOICE_PHOTO_REQUIRED"],
  m6_inspection:  ["VOICE_ROOM_READY", "VOICE_ROOM_DIRTY"],
  m7_emergency:   ["VOICE_SUPERVISOR_REQUIRED"],
};

/** LessonSurface — dispatches on `action` to a HK-OS primitive. */
function LessonSurface({ action, room, onSuccess, onFailure }) {
  const [emergencyOpen, setEmergencyOpen] = useState(action === "acknowledge_emergency");

  useEffect(() => {
    setEmergencyOpen(action === "acknowledge_emergency");
  }, [action]);

  if (action === "acknowledge_emergency") {
    return (
      <AttentionOverlay
        open={emergencyOpen}
        variant="emergency"
        headline={room} subline="Emergency Task"
        voiceKey="VOICE_SUPERVISOR_REQUIRED"
        ctaLabelKey="VOICE_START_TASK"
        onDismiss={() => setEmergencyOpen(false)}
        ctaOnClick={onSuccess}
        testid="lesson-attention-emergency"
      />
    );
  }

  if (action === "scan_room") {
    return (
      <div className="grid gap-4">
        <NextTaskCard
          roomNumber={room} floorLabel={`Floor ${room[0]} · Practice`}
          taskTypeKey="VOICE_SCAN_ROOM"
          badges={[{ label: "Training", tone: "info" }]}
          ctaKey="VOICE_SCAN_ROOM"
          onStart={onSuccess}
          testid="lesson-next-task"
        />
        <ScanRoomButton onLaunch={onSuccess} testid="lesson-scan-btn" />
      </div>
    );
  }

  if (action === "take_photo") {
    return (
      <div className="rounded-3xl border-2 border-slate-200 bg-white p-6 space-y-4">
        <div className="text-[18px] font-bold text-slate-900">Room {room}</div>
        <PhotoEvidenceUploader
          onUpload={async ({ onProgress }) => {
            for (let i = 1; i <= 5; i++) {
              await new Promise((r) => setTimeout(r, 120));
              onProgress?.(i / 5);
            }
            return { ok: true, media_id: "training-photo" };
          }}
          onChange={({ photos }) => { if (photos.length > 0) onSuccess(); }}
          testid="lesson-photo"
        />
        <div className="text-[12px] text-slate-500">Take at least one photo to continue.</div>
      </div>
    );
  }

  if (action === "record_note" || action === "log_lost_found") {
    return (
      <div className="rounded-3xl border-2 border-slate-200 bg-white p-6 flex flex-col items-center gap-4">
        <div className="text-[18px] font-bold text-slate-900">Room {room}</div>
        <VoiceRecorderButton
          promptKey={action === "log_lost_found" ? "VOICE_LOST_FOUND" : "VOICE_RECORD_NOTE"}
          maxSeconds={10}
          onStop={(blob) => { if (blob) onSuccess(); else onFailure?.(); }}
          testid="lesson-recorder"
        />
      </div>
    );
  }

  if (action === "start_task" || action === "finish_task") {
    return (
      <div className="rounded-3xl border-2 border-slate-200 bg-white p-6 space-y-4">
        <NextTaskCard
          roomNumber={room} floorLabel="Practice"
          taskTypeKey="VOICE_ROOM_DIRTY"
          badges={[{ label: "Training", tone: "info" }]}
          ctaKey={action === "start_task" ? "VOICE_START_TASK" : "VOICE_FINISH_TASK"}
          onStart={onSuccess}
          testid="lesson-task"
        />
      </div>
    );
  }

  if (action === "inspect_pass" || action === "inspect_fail") {
    return (
      <div className="rounded-3xl border-2 border-slate-200 bg-white p-6 space-y-4">
        <div className="text-[18px] font-bold text-slate-900">Inspect Room {room}</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <TaskCompletionButton voiceKey="VOICE_ROOM_READY" icon={CheckCircle2}
            tone="success"
            onClick={() => (action === "inspect_pass" ? onSuccess() : onFailure())}
            testid="lesson-inspect-pass" />
          <TaskCompletionButton voiceKey="VOICE_ROOM_DIRTY" icon={RefreshCw}
            tone="danger"
            onClick={() => (action === "inspect_fail" ? onSuccess() : onFailure())}
            testid="lesson-inspect-fail" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border-2 border-dashed border-slate-300 p-6 text-slate-500"
          data-testid="lesson-unknown">
      Unknown action: {action}
      <TaskCompletionButton voiceKey="VOICE_NEXT_TASK" onClick={onSuccess} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Assessment Runner — 10 randomized lessons
// ═══════════════════════════════════════════════════════════════════
function AssessmentRunner({ session, onExit, onResult }) {
  const { play } = useVoiceRegistry();
  const [lessons, setLessons] = useState([]);
  const [step, setStep] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    (async () => {
      try {
        const r = await apiGet(`/training/session/${session.id}/assessment`);
        setLessons(r.lessons || []);
        play("VOICE_ASSESSMENT_START").catch(() => {});
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Cannot start assessment");
        onExit();
      }
    })();
  }, [session.id, onExit, play]);

  const submit = useCallback(async (finalCorrect) => {
    setSubmitting(true);
    try {
      const r = await apiPost(`/training/session/${session.id}/assessment/submit`, {
        correct: finalCorrect, total: lessons.length,
        duration_ms: Date.now() - startedAt.current,
      });
      if (r.passed) play("VOICE_ASSESSMENT_PASS").catch(() => {});
      else play("VOICE_RETRY").catch(() => {});
      onResult(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }, [session.id, lessons.length, play, onResult]);

  if (!lessons.length) {
    return (
      <div className="min-h-[300px] flex items-center justify-center text-slate-500 gap-3">
        <Loader2 className="w-6 h-6 animate-spin" /> Preparing assessment…
      </div>
    );
  }

  const cur = lessons[step];
  const room = PRACTICE_ROOMS[step % PRACTICE_ROOMS.length];
  const advance = (ok) => {
    const next = correct + (ok ? 1 : 0);
    setCorrect(next);
    if (step + 1 >= lessons.length) submit(next);
    else setStep(step + 1);
  };

  return (
    <div className="space-y-6" data-testid="assessment-runner">
      <div className="flex items-center gap-3">
        <button type="button" onClick={onExit}
                  data-testid="assessment-exit"
                  className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700">
          ← Back
        </button>
        <div className="text-[12px] font-bold uppercase tracking-widest text-emerald-700">
          Final Practical Assessment
        </div>
        <div className="ml-auto text-[13px] font-bold text-slate-700 tabular-nums"
              data-testid="assessment-progress">
          {step + 1} / {lessons.length} · {correct} correct
        </div>
      </div>
      <VoiceInstructionBar voiceKey={cur.voice_key} autoPlay variant="hero"
                             progress={(step + 1) / lessons.length}
                             testid="assessment-instruction" />
      <LessonSurface
        action={cur.action}
        room={room}
        onSuccess={() => advance(true)}
        onFailure={() => advance(false)}
      />
      {submitting && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/70 text-white">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Certification Screen
// ═══════════════════════════════════════════════════════════════════
function CertificationScreen({ session, onDone }) {
  const cert = session?.certification;
  const badges = session?.badges || [];
  return (
    <div className="space-y-6" data-testid="certification-screen">
      <div className="rounded-3xl border-2 border-emerald-300 bg-emerald-50 p-8 text-center">
        <Award className="w-24 h-24 text-emerald-600 mx-auto mb-4" strokeWidth={2} />
        <div className="text-[13px] font-bold uppercase tracking-widest text-emerald-800">
          Housekeeping Ready
        </div>
        <div className="text-[36px] font-black text-emerald-900 mt-1">
          Certified
        </div>
        <div className="text-[14px] text-emerald-800 mt-2 tabular-nums">
          {cert ? new Date(cert.issued_at).toLocaleString() : "—"}
        </div>
        <div className="text-[14px] text-emerald-800 mt-1">
          Score: {(cert?.score || session?.score || 0).toFixed(0)}%
        </div>
        <div className="mt-4">
          <VoicePlayer voiceKey="VOICE_CERTIFIED" size="lg" showText
                         testid="cert-voice" />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {badges.map((b) => (
          <div key={b.key}
                data-testid={`badge-${b.key}`}
                className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 flex flex-col items-center">
            <Trophy className="w-10 h-10 text-amber-600 mb-1" strokeWidth={2} />
            <div className="text-[13px] font-bold text-amber-900 text-center">
              {b.label}
            </div>
          </div>
        ))}
      </div>
      <TaskCompletionButton voiceKey="VOICE_NEXT_TASK" tone="primary"
                              icon={ArrowRight} onClick={onDone}
                              testid="cert-done-btn" />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Root
// ═══════════════════════════════════════════════════════════════════
export default function HkTrainingApp() {
  return (
    <VoiceRegistryProvider locale="bn-IN" autoWarmup>
      <HkTrainingRoot />
    </VoiceRegistryProvider>
  );
}

function HkTrainingRoot() {
  const [session, setSession] = useState(null);
  const [modules, setModules] = useState([]);
  const [view, setView] = useState("home");   // home | module | assessment | cert
  const [activeModuleId, setActiveModuleId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assessResult, setAssessResult] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cur, mods] = await Promise.all([
        apiGet("/training/session/current"),
        apiGet("/training/modules"),
      ]);
      setSession(cur.session);
      setModules(mods.modules || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load training");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const isSupervisor = useMemo(() => {
    // Best-effort: the /training/supervisor/board endpoint requires the
    // hotel_profiles.create permission; if we're admin we'll always be OK.
    // We simply expose the CTA and let the guard fail on click if not.
    return true;
  }, []);

  const startSession = useCallback(async () => {
    // Pick the first hotel from a lightweight probe.
    try {
      const { data: hotels } = await axios.get(`${API}/api/hotels`, { withCredentials: true });
      const hotel_id = (hotels || [])[0]?.id;
      if (!hotel_id) {
        toast.error("No hotel found for training");
        return;
      }
      const r = await apiPost("/training/session/resume", { hotel_id });
      setSession(r.session);
      toast.success(r.resumed ? "Resumed training" : "Training started");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cannot start training");
    }
  }, []);

  const openModule = (mid) => {
    setActiveModuleId(mid);
    setView("module");
  };

  const finishedModule = (s) => {
    setSession(s);
    setView("home");
    toast.success("Module complete");
  };

  const startAssessment = () => setView("assessment");

  const assessmentResult = (r) => {
    setSession(r.session);
    setAssessResult(r);
    if (r.passed) setView("cert-eligible");
    else setView("home");
  };

  const certify = async () => {
    try {
      const r = await apiPost(`/training/session/${session.id}/certify`);
      setSession(r.session);
      setView("cert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Certify failed");
    }
  };

  const resetTraining = async () => {
    if (!session) return;
    if (!confirm("Reset all training progress?")) return;
    try {
      // Use supervisor reset endpoint on my own session (works because
      // admin has * permission).
      const r = await apiPost(`/training/supervisor/session/${session.id}/reset`);
      setSession(r.session);
      setView("home");
      setAssessResult(null);
      toast.success("Training reset");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const gotoSupervisor = () => { window.location.assign("/hk/training/supervisor"); };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500 gap-3">
        <Loader2 className="w-6 h-6 animate-spin" /> Loading training…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-sky-600">
              HK-2 · Phase 5
            </div>
            <h1 className="text-[24px] font-black text-slate-900 leading-tight"
                data-testid="training-title">
              Training Mode
            </h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <OfflineSyncIndicator />
            <VoiceWarmupManager hideWhenReady />
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {view === "home" && (
          <HomeScreen
            session={session} modules={modules}
            onStart={startSession}
            onModuleOpen={openModule}
            onAssessment={startAssessment}
            onReset={resetTraining}
            onCertify={certify}
            isSupervisor={isSupervisor}
            onOpenSupervisor={gotoSupervisor}
          />
        )}
        {view === "module" && (
          <ModuleRunner
            session={session} moduleId={activeModuleId} modules={modules}
            onExit={() => setView("home")}
            onFinishedModule={finishedModule}
          />
        )}
        {view === "assessment" && (
          <AssessmentRunner
            session={session}
            onExit={() => setView("home")}
            onResult={assessmentResult}
          />
        )}
        {view === "cert-eligible" && (
          <div className="space-y-4 text-center rounded-3xl border-2 border-emerald-300 bg-emerald-50 p-8"
                data-testid="cert-eligible-screen">
            <PartyPopper className="w-16 h-16 text-emerald-600 mx-auto" />
            <div className="text-[26px] font-black text-emerald-900">
              You Passed
            </div>
            <div className="text-[15px] text-emerald-800">
              Score: {(assessResult?.accuracy ? assessResult.accuracy * 100 : 0).toFixed(0)}%
            </div>
            <VoicePlayer voiceKey="VOICE_ASSESSMENT_PASS" size="lg" showText
                           testid="cert-eligible-voice" />
            <TaskCompletionButton voiceKey="VOICE_CERTIFIED" icon={Award}
                                     tone="success" onClick={certify}
                                     testid="cert-eligible-btn" />
          </div>
        )}
        {view === "cert" && (
          <CertificationScreen session={session} onDone={() => setView("home")} />
        )}
      </main>
    </div>
  );
}
